import Vue from "vue";
import Vuex from "vuex";
Vue.use(Vuex);

import _ from "lodash";
import FETIASEntity from "../../business/FETIASEntity";
import nativeFormula, { NATIVE_ENGINE_VERSION } from "../../business/nativeFormula";

import { diff, patch } from 'jsondiffpatch'
const jdpModule = require("jsondiffpatch");

import VueRouter from "vue-router";
Vue.use(VueRouter);

import commons from '../commons/commons';
import { commonStore } from "./commonStore";
import LocalStorageService from "./LocalStorageService";
import selectionCache from "./selectionCache";
import { xCall } from './xClient';

let summ = function (expr, list) {
  return list.reduce(function (accumulator, row) {
    return accumulator + expr(row);
  }, 0);
}

/* Native FE-compute model + recompute live in ./nativeCompute (extracted so they can be unit-tested
   without standing up the Vuex store). The store owns the state they read/write. */
import { buildNativeModel, nativeRecompute, stripNativeTriggers, applyElementFormChange } from "./nativeCompute";

import Axios from "axios";
import webAppConfig from "../commons/webApp.config";

import router from '../routers/clientRouters';

function isRunAsOrDemoRequest(state) {
  const wsId = state &&
    state.client &&
    state.client.defaults &&
    state.client.defaults.headers &&
    state.client.defaults.headers.wsId;

  return Boolean(
    typeof wsId === "string" &&
    (
      wsId.indexOf("runas:") === 0 ||
      wsId.indexOf("demo:") === 0
    )
  );
}

function isXFormSelection(data) {
  return Boolean(data && data.x === true);
}

function shouldUseXFormEndpoint(state, payload) {
  if (isRunAsOrDemoRequest(state)) return false;
  if (payload.isShareRoute) return false;
  // No needSync veto here. Routing is decided per-activity by compatibleStandards / activityIds
  // below, which the backend populates only for forms the port can actually serve — membership
  // means "not needSync" by construction (x:true / a compatible standard is set only when
  // CanServeForm passes, which fails on any needSync form). state.needSync is the LAST-loaded
  // form's flag (global/sticky) and says nothing about the form being opened now, so gating on
  // it can only mis-route a servable form to legacy: e.g. a view or an x:true custom action
  // opened right after an edit (needSync) would be bounced despite being servable. The 501
  // fallback (SS05901) covers any route the per-activity flags misjudge.
  if (payload.listingId == 'everything' || payload.listingId == 'archive') return false; // The 'everything' listing (admin-only) is not yet ported.
  if (payload.query && payload.query.behalf) return false;
  if (!payload.moduleId) return false;

  const entry = state.xFormMap && state.xFormMap[payload.listingId || payload.moduleId];
  if (!entry) return false;

  if (entry.compatibleStandards.has(payload.activityId)) return true;
  if (entry.activityIds.has(payload.activityId)) return true;

  return false;
}

function shouldUseXSelectEndpoint(state, data, isShareForm) {
  if (isShareForm) return false;
  if (isRunAsOrDemoRequest(state)) return false;
  return isXFormSelection(data); // reads data.x — call BEFORE `delete data.x`
}

function buildActivityFormPayload(payload) {
  return {
    moduleId: payload.moduleId,
    listingId: payload.listingId,
    activityId: payload.activityId,
    entryId: payload.entryId,
    locusId: payload.locusId,
    query: payload.query,
    nativeEngineVersion: NATIVE_ENGINE_VERSION
  };
}

async function postActivityForm(state, payload, config) {
  const formPayload = buildActivityFormPayload(payload);
  const useXForm = shouldUseXFormEndpoint(state, payload);
  return xCall(state.client, 'form', { payload: formPayload, gate: useXForm, config });
}

export const classificationStore = new Vuex.Store({
  state: {
    ...commonStore.state,
    users: [],
    states: [],
    previousClone: null,
    submissionClone: null,
    interimClone: null,
    appliedChange: false,
    needSync: false,
    baseServer: "",
    form: null,
    originalForm: null,
    formData: null,
    originalFormData: null,
    formDesign: null,
    locusId: null,
    vectorId: null,
    vector: null,
    activityId: null,
    activityComplexity : null,
    documentId: null,
    token: LocalStorageService.getAccessToken(),
    currentUsername: null,
    query: null,
    initial: null,
    synchronizing: false,
    backendTriggers: null,
    frontTriggers: null,
    /* Native FE-compute (P1): the bundle from the form response and its compiled, topologically
       ordered model. When nativeBundle.fullyClientComputable is true the form computes every
       formula locally on each edit and never calls /formsync. */
    nativeBundle: null,
    nativeModel: null,
    syncError: false,
    syncErrorRetry: 0,
    syncLastTime: null,
    /* §4.3.1 live-form GNey fill: null, or { status: 'pending'|'done'|'error', label, nodeId,
       actionId, message, filled, undoValues, applied, awaitingApplySync }. Rendered by
       FTSTeammateFillStrip; written only by the teammateFill* actions. */
    teammateFill: null,
    /* D4 result cache: nodeId|actionId|file-values -> returned values. Per open form. */
    teammateFillCache: {},
    activityName: null,
    workspace: null,
    polling: null,
    formAbortController: null,
    syncAbortController: null,
    cachedForms: {},
    xFormMap: {}
  },
  watch: {
    formData(newValue, oldValue) {
      console.log("change");
    }
  },
  mutations: {
    ...commonStore.mutations,
    reset(state) {
      state.activityId = null;
      state.activityComplexity = null;
    },
    setup(state, data) {
      if (data.baseServer) state.baseServer = data.baseServer;
      if (data.formData) state.formData = data.formData;

      if (state.formAbortController) {
        state.formAbortController.abort();
        state.formAbortController = null;
      }
      state.formAbortController = new AbortController();

      if (state.syncAbortController) {
        state.syncAbortController.abort();
        state.syncAbortController = null;
      }
      state.syncAbortController = new AbortController();

      state.locusId = data.locusId;
      state.vectorId = data.vectorId;
      state.activityId = data.activityId;
      state.users = data.users;
      state.query = data.query;
      state.initial = true;

      if (localStorage['ls.authorizationData']) {
        state.currentUsername = JSON.parse(localStorage['ls.authorizationData']).userName;
      }
    },
    setServer(state, data) {
      if (data.baseServer != null) state.baseServer = data.baseServer;
    },
    setLocusId(state, data) {
      state.locusId = data;
    },
    setVector(state, data) {
      if (data.vectorId != null) {
        state.vectorId = data.vectorId;
        state.vector = {
          id: data.vectorId,
          name: data.vectorName,
          emoji: data.vectorEmoji
        }
      }
    },
    setVectorId(state, data) {
      state.vectorId = data;
    },
    setWorkspace(state, data) {
      state.workspace = data;
      state.client.defaults.headers['workspace'] = state.workspace;
    },
    basicSetup(state, data) {
      state.baseServer = data.baseServer;
      // state.token = data.token;
      state.documentId = data.documentId;
      state.vectorId = data.vectorId;
    },
    loadForm(state, form) {
      state.syncLastTime = new Date()
      state.previousClone = null;
      state.form = form;
      state.originalForm = null;
      state.documentId = null;
      /* a fill (or its strip, or its result cache) never outlives the form it filled */
      state.teammateFill = null;
      state.teammateFillCache = {};
    },
    loadFormData(state, formData) {
      state.formData = formData;
    },
    loadOriginalFormData(state, formData) {
      state.originalFormData = formData;
    },
    updateDocumentId(state, payload) {
      if (payload) {
        state.documentId = payload.documentId;
        state.vectorId = payload.vectorId;
      } else {
        state.documentId = null;
      }

    },
    syncFormData(state, formData) {
      state.syncLastTime = new Date()
      state.appliedChange = true;
      state.formData = formData;
    },
    updateForm(state, form) {
      if (!state.originalForm) {
        state.originalForm = state.form;
      }

      state.form = form;
    },
    revertForm(state) {
      state.form = state.originalForm;
    },
    syncTriggers(state, data) {
      state.frontTriggers = data.frontTriggers;
      state.backendTriggers = data.backendTriggers;
    },
    setNativeBundle(state, bundle) {
      state.nativeBundle = bundle || null;
      state.nativeModel = bundle ? buildNativeModel(bundle) : null;
    },
    storeFormDesign(state, formDesign) {
      state.formDesign = formDesign;
      state.vectorId = formDesign.vectorId;
      state.needSync = formDesign.needSync;
    },
    setNeedSync(state, needSync) {
      state.needSync = needSync;
    },
    resetApplyChange(state) {
      state.appliedChange = false;
    },
    setUsers(state, users) {
      state.users = users;
    },
    setStates(state, states) {
      state.states = states
    },
    setPolling(state, polling) {
      state.polling = polling;
    },
    setXFormMap(state, { listingId, moduleId, compatibleStandards, activities }) {
      const key = listingId || moduleId;
      if (!key) return;
      Vue.set(state.xFormMap, key, {
        compatibleStandards: new Set(Array.isArray(compatibleStandards) ? compatibleStandards : []),
        activityIds: new Set(
          Array.isArray(activities)
            ? activities.filter(a => a && a.x === true).map(a => a.id)
            : []
        )
      });
    }
  },
  getters: {
    formData: state => state.formData,
    originalFormData: state => state.originalFormData,
    uploadFormData: (state) => (payload) => {
      return {
        vectorId: state.vectorId,
        fieldName: payload.fieldName,
        fieldId: payload.fieldId,
        type: payload.formType,
        workDesignId: state.formDesign ? state.formDesign.workDesignId : null
      };
    },
    syncError: state => state.syncError,
    currentUserProfileUrl: (state) => {
      var user = state.users.find(x => x.userName == state.currentUsername);
      if (
        user &&
        user.profilePictureUrl &&
        user.profilePictureUrl.indexOf("ProfilePics") > -1
      ) {
        return state.baseServer + user.profilePictureUrl;
      }

      return '';
    },
    getCachedForm: (state) => ({ moduleId, activityId }) => {
      const key = `${moduleId}-${activityId}`;
      return state.cachedForms[key] || null;
    }
  },
  actions: {
    ...commonStore.actions,
    reset({ commit }) {
      commit('reset');
    },
    polling({ commit }, payload) {
      commit('setPolling', payload.polling);
    },
    setUsers({ commit }, payload) {
      let users = payload.users.map((x) => (
        Object.assign(x, {
          username: x.username || x.userName
        })
      ));
      commit("setUsers", users);
    },
    setStates({ commit }, payload) {
      commit("setStates", payload);
    },
    resetApplyChange({ commit }) {
      commit("resetApplyChange");
    },
    setBaseServer({ commit }, payload) {
      commit('setServer', payload);
    },
    setLocusId({ commit }, payload) {
      commit('setLocusId', payload);
    },
    setVector({ commit }, payload) {
      commit('setVector', payload);
    },
    setWorkspace({ commit }, payload) {
      commit('setWorkspace', payload)
    },
    basicSetup({ commit }, payload) {
      commit('basicSetup', payload);
    },
    setFormData({ commit }, payload) {
      commit("loadFormData", payload);
    },
    setOriginalFormData({ commit }, payload) {
      commit("loadOriginalFormData", payload);
    },
    resetActivityForm({ commit }) {
      commit("loadForm", { rows: [{ items: [] }] });
      commit("loadFormData", {});
      commit("loadOriginalFormData", {});
    },
    async loadDocumentForm({ state }, payload) { /* never read */
      return await state.client.post(`/api/activity/form`, payload);
    },
    async loadDocumentLegacy({ state }, payload) { /* deprecated, never read */
      return await state.client.get(payload.url);
    },
    async loadViewForm({ state, commit }, payload) {
      const requestPayload = {
        moduleId: payload.moduleId,
        listingId: payload.listingId,
        activityId: payload.activityId,
        entryId: payload.entryId,
        locusId: payload.locusId /* to-do delete */,
        query: payload.query
      };

      const useXForm = shouldUseXFormEndpoint(state, payload);
      const res = await xCall(state.client, 'form', { payload: requestPayload, gate: useXForm });

      if (res.data.footer) {
        commit('setXFormMap', {
          listingId: payload.listingId,
          moduleId: payload.moduleId,
          compatibleStandards: res.data.footer.compatibleStandards,
          activities: res.data.footer.activities
        });
      }

      let form = res.data.classificationForm.design;
      let formData = res.data.classificationForm.default;

      if (Array.isArray(res.data.formChanges)) {
        res.data.formChanges.forEach(change => {
          let element = commons.findFormField(
            form &&
            form.rows,
            change,
            formData
          );
          applyElementFormChange(element, change);
        });
      }

      return {
        viewData: {
          form: form,
          formData: formData
        },
        header: res.data.header,
        footer: res.data.footer
      };
    },
    loadActivityFormCache({ state, commit }, payload) {
      let { setup, data } = payload;
      let classificationForm = data.classificationForm;
      state.syncError = false;
      commit("setNeedSync", false);
      commit("setup", setup); 
      state.synchronizing = true;

      if (data.header && data.header.moduleId) {
        commit("setVectorId", data.header.moduleId);
      }

      if (classificationForm) {
        if (setup.username) {
          classificationForm.default['username'] = setup.username;

          const user = state.users.find(x => x.username == setup.username);
          if (user) {
            classificationForm.default['name'] = user.displayName;
          }
        }

        const defaultClone = _.cloneDeep(classificationForm.default);

        commit("loadForm", classificationForm.design);
        commit("loadFormData", defaultClone);
        commit("loadOriginalFormData", defaultClone);
        commit("syncTriggers", classificationForm);
        commit("setNativeBundle", classificationForm.nativeBundle);
        state.previousClone = defaultClone;
        commit("setNeedSync", classificationForm.needSync);
      }

      state.activityId = setup.activityId;
      state.activityName = setup.activity;
      state.synchronizing = false;

      if (data.hasOwnProperty("complexity")) {
        state.activityComplexity = payload.data.complexity;
      } else {
        state.activityComplexity = null;
      }
    },
    async loadActivitySnapshot(context, payload) {
      const res = await postActivityForm(context.state, payload);

      const classificationForm = res && res.data && res.data.classificationForm;
      if (
        !classificationForm ||
        !classificationForm.default ||
        typeof classificationForm.default !== 'object' ||
        Array.isArray(classificationForm.default)
      ) {
        throw new Error('Activity form response is missing a complete default snapshot');
      }

      return _.cloneDeep(classificationForm.default);
    },
    async loadActivityForm(context, payload) {
      try {
        if (!payload.hasCache) {
          context.state.syncError = false;
          context.commit("setNeedSync", false);
          context.commit("setNativeBundle", null);
          context.commit("setup", payload);
          context.state.synchronizing = true;
          context.commit("loadForm", { rows: [{ items: [] }] });
          context.commit("loadFormData", {});
          context.commit("loadOriginalFormData", {});
        }

        let res = null;
        let classificationForm = null;
        let needCache = !!payload.enableCache;

        if (payload.route) {
          let route = payload.route;
          res = await Axios.post(`${webAppConfig.baseURL}/api/activity/form`, {
            query: payload.route.query,
            /* handshake: native-formula engine version this client can evaluate */
            nativeEngineVersion: NATIVE_ENGINE_VERSION
          },
            {
              headers: {
                Authorization: `inishare ${payload.route.params.id}:${payload.route.query.code}`
              }
            })

          if(res.data.header && res.data.header.activity) {
            payload.activityId = res.data.header.activity.id
            payload.activity = res.data.header.activity.name
          } else if (res.data.header.id) {
            payload.activityId = 'edit';
            payload.activity = 'edit';
          } else {
            payload.activityId = 'create';
            payload.activity = 'create';
          }
        } else if (payload.mockForm) {
          /* internal-alpha mock: use the canned form schema verbatim, no network call — lets a fake
             module render a real, fillable form for teammate card/UI testing without a backend. */
          res = { data: payload.mockForm };
          needCache = false;
        } else {
          let config = null;
          if (context.state.formAbortController) {
            config = {
              signal: context.state.formAbortController.signal
            }
          }

          res = await postActivityForm(
            context.state,
            { ...payload, locusId: context.state.locusId }, /* to-do delete */
            config
          );

        }

        classificationForm = _.cloneDeep(res.data.classificationForm);

        if (res.data.header && res.data.header.moduleId) {
          context.commit("setVectorId", res.data.header.moduleId);
        }

        if (classificationForm) {
          if (payload.username) {
            classificationForm.default['username'] = payload.username;

            var user = context.state.users.find(x => x.username == payload.username);
            if (user) {
              classificationForm.default['name'] = user.displayName;
            }
          }

          if (Array.isArray(res.data.formChanges)) {
            res.data.formChanges.forEach(change => {
              // get
              let element = commons.findFormField(
                classificationForm.design &&
                classificationForm.design.rows,
                change,
                classificationForm.default
              );
              // set
              applyElementFormChange(element, change);
            });
          }

          context.commit("loadForm", classificationForm.design);
          context.commit("syncTriggers", classificationForm);
          context.commit("setNativeBundle", classificationForm.nativeBundle);
          context.commit("setNeedSync", classificationForm.needSync);

          if (
            !payload.enableCache ||
            !(
              payload.activityId == "create" && 
              !classificationForm.needSync && 
              !Object.keys({...res.data.classificationForm.default}).length
            )
          ) {
            needCache = false;
            context.commit("loadFormData", classificationForm.default);
            /* compute initial computed-field values on the client so they show immediately and the
               dirty-check baseline already reflects them (the server recomputes authoritatively on
               submit regardless). */
            if (context.state.nativeModel) nativeRecompute(context);
            context.commit("loadOriginalFormData", _.cloneDeep(context.state.formData));
            context.state.previousClone = _.cloneDeep(context.state.formData);
          }
        }

        context.state.activityId = payload.activityId;
        context.state.activityName = payload.activity;
        context.state.synchronizing = false;

        /* SS06058 / SS06074: a backend script or automation that sets Interaction['message']
           on the INITIAL pass (form open) now travels on the form response, the same way the
           sync response carries it — show it as soon as the form is loaded. */
        if (res.data.interaction && res.data.interaction.message != null) {
          alert(res.data.interaction.message);
        }

        if (res.data.hasOwnProperty("complexity")) {
          context.state.activityComplexity = res.data.complexity;
        } else {
          context.state.activityComplexity = null;
        }

        let responseData = {
          classificationForm: res.data.classificationForm,
          header: res.data.header,
          footer: res.data.footer,
          needSync: context.state.needSync,
          needCache: needCache
        }

        if (context.state.activityComplexity != null) {
          responseData.complexity = context.state.activityComplexity;
        }

        return responseData;
      } catch(err) {
        throw err;
      }
    },
    setCachedForm({ state }, { moduleId, activityId, data }) {
      const key = `${moduleId}-${activityId}`;
      state.cachedForms[key] = data;
    },
    clearCachedForm({ state }, { moduleId, activityId }) {
      const key = `${moduleId}-${activityId}`;
      delete state.cachedForms[key];
    },
    setXFormMap({ commit }, payload) {
      commit('setXFormMap', payload);
    },
    async searchEntry(context, payload) {
      let data = {};
      let config = {};

      if (payload.data && payload.config && Object.keys(payload).length === 2) {
        data = payload.data;
        config = payload.config;
      } else {
        data = payload;
      }

      if (context.state.documentId) {
        data.documentId = context.state.documentId;
      }

      const isShareForm = Boolean(
        window.router &&
        window.router.currentRoute &&
        window.router.currentRoute.name == 'shareForm'
      );

      // guest/share-link and runas/demo sessions always use legacy
      const useXSelect = shouldUseXSelectEndpoint(context.state, data, isShareForm);

      // Selection cache (docs/fe-caching-and-formula-performance-design.md §4/§5). Snapshot the
      // cache-relevant fields BEFORE `delete data.x` below strips the eligibility flag. Share /
      // runas / demo sessions are never cached (different auth, no SignalR invalidation).
      const baseServer = context.state.baseServer;
      const canCache = !isShareForm && !isRunAsOrDemoRequest(context.state);
      const cacheData = canCache ? {
        vectorId: data.vectorId,
        activityId: data.activityId,
        fieldId: data.fieldId,
        reference: data.reference,
        connectionId: data.connectionId,
        x: data.x,
        id: data.id,
        text: data.text,
        currentPage: data.currentPage,
        pageSize: data.pageSize
      } : null;
      const cacheEligible = canCache && selectionCache.isRequestCacheable(cacheData);

      if (cacheEligible) {
        const cached = selectionCache.getOptions(cacheData, baseServer);
        if (cached) {
          // mirror the axios response shape callers expect; readRecord re-parses a fresh copy each
          // call so callers can safely mutate the option objects (e.g. date formatting).
          return { data: cached };
        }
      }

      const requestStartedAt = cacheEligible ? new Date().getTime() : null;
      const cacheResult = (res) => {
        if (cacheEligible && res) {
          selectionCache.setOptions(cacheData, res.data, baseServer, requestStartedAt);
        }
        return res;
      };

      if (data.hasOwnProperty("x")) {
        delete data.x;
      }

      if (isShareForm) {
        return await Axios.post(`${webAppConfig.baseURL}/api/activity/formselection`, data, {
          headers: {
            Authorization: `inishare ${window.router.currentRoute.params.id}:${window.router.currentRoute.query.code}`
          },
          ...config
        });
      } else {
        return cacheResult(await xCall(context.state.client, 'select', { payload: data, gate: useXSelect, config }));
      }
    },
    cartToForm(context, payload) {
      //find form field,
      let rows = this.formData[payload.fields[0].fieldName];
      payload.items.forEach(item => {
        rows.push({
          id: commons.newUUID(),
        });
      });

    },
    eventTriggered({ dispatch }, payload) {
      dispatch('synchronize', {
        activity: payload.activity,
        eventName: payload.eventName,
        eventPayload: payload.eventPayload
      });
    },
    async synchronize(context, payload) {
      /* Native FE-compute fast path: when every formula is client-computable, evaluate locally and
         never touch /formsync. eventName changes still go to the server (events can run automation).
         Partially-computable forms fall through to the normal sync path below. */
      if (context.state.nativeModel
        && context.state.nativeModel.fullyClientComputable
        && !payload.eventName) {
        nativeRecompute(context);
        return;
      }

      console.log(`synchronize: ${payload.polling}, current: ${context.state.polling}`);

      if (payload.polling != context.state.polling && !payload.eventName) {
        console.log('secondary clear');
        clearInterval(payload.polling);
        return;
      }

      /* Computed fields are always evaluated natively on the client (min(server, FE) version), so they
         are skipped on the sync + trigger paths. The fully-client-computable form already returned via
         the fast path above; this partial form recomputes its client-computable subset here for live
         UX, then lets the existing formsync handle the rest (non-computed form formulas, control
         formulas, backend automation). */
      if (context.state.nativeModel) {
        nativeRecompute(context);
      }

      /* Strip the targets native recompute owns off BOTH trigger lists. A backend trigger whose only
         targets are native computed fields (e.g. "when the table changes, recompute No + Subtotal")
         must not force a /formsync round-trip -- the client computes those and the server recomputes
         on submit. A trigger with a server target (e.g. "when Product changes, recompute Description")
         is kept, so genuine server formulas still sync. Same stripping keeps a native computed field
         off the legacy eval path so it is never evaluated twice (native + eval) and diverge. */
      let backendTriggers = context.state.nativeModel
        ? stripNativeTriggers(context.state.backendTriggers, context.state.nativeModel)
        : context.state.backendTriggers;
      let frontTriggers = context.state.nativeModel
        ? stripNativeTriggers(context.state.frontTriggers, context.state.nativeModel)
        : context.state.frontTriggers;
      let sendBack = false;

      let inputFields = commons.getAllElements(null, context.state.form);

      let currentClassification = new FETIASEntity(context.state.formData, inputFields);
      let previousClassification = new FETIASEntity(context.state.previousClone, inputFields);
      let interimClassification = new FETIASEntity(context.state.interimClone, inputFields);
      let formData = currentClassification;

      /* SS06057: an automation that reads no form field ("show a message on any change") resolves
         to an EMPTY trigger list, which the declared-trigger design (SS06019) read as "nothing to
         sync on" - so the web never synced and the message never appeared, while the App syncs on
         every edit of a form that needs syncing. Ruling 2026-09-07: the web matches the App - the
         server declaring NO trigger for a form that still needs syncing means "sync on any change".
         Judged on the server's own list, not the native-stripped copy: a list that only emptied
         because the client computes every target is a genuine "no round-trip needed". A form that
         needs no sync at all keeps its empty list and stays off /formsync. */
      const syncOnAnyChange = context.state.needSync
        && Array.isArray(context.state.backendTriggers)
        && context.state.backendTriggers.length === 0;

      if (syncOnAnyChange) {
        sendBack = true;
      } else if (backendTriggers != null && context.state.previousClone != null) {
        /* On a native form, judge a per-row column trigger by whether a row's value actually changed,
           not by row count: adding an empty row (or removing one) does not, by itself, change a
           per-row server formula's input -- the native columns recompute locally and no /formsync is
           needed until the real input (e.g. Product) is set. Non-native forms keep the old behavior.
           A v4 CONTROL-ONLY model (no computed values/columns) must NOT enable the skip: its table
           columns are server-computed, so an added row still needs the sync. */
        const skipEmptyNewRows = !!(context.state.nativeModel
          && (context.state.nativeModel.order.length || context.state.nativeModel.columnTables.length));
        sendBack = backendTriggers.some(field => {
          return currentClassification.hasChange(previousClassification, field.primary, field.secondary, skipEmptyNewRows)
        })
      } else if (context.state.previousClone == null) {
        sendBack = true;
      } else if (backendTriggers == null) {
        sendBack = true;
      }

      if (!sendBack && frontTriggers != null) {
        let recalculate = false;
        frontTriggers.forEach(trigger => {
          let change = context.state.interimClone == null
            || currentClassification.hasChange(interimClassification, trigger.primary, trigger.secondary);
          if (change) {
            recalculate = true;
            let field = function (displayName) {
              return formData.field(displayName)
            }

            if (trigger.secondary) {
              //table or list
              currentClassification.field(trigger.primary).forEach(row => {
                trigger.targets.forEach(targetField => {
                  try {
                    if (trigger.within) {
                      eval(`row.set('${targetField.secondary}', ${targetField.frontEndScript})`)
                    } else {
                      eval(`formData.set('${targetField.primary}', ${targetField.frontEndScript})`)
                    }
                  } catch (ex) {
                    console.log(`${trigger.primary}>${targetField.secondary}: ${targetField.primary}, ${ex}`)
                  }
                });
              })
            } else {
              //todo: check if different
              trigger.targets.forEach(targetField => {
                try {
                  if (targetField.frontEndScript == 'rowNumber()' || targetField.frontEndScript == 'rowAutoNumber') {
                    let no = 1;
                    currentClassification.field(trigger.primary).forEach(row => {
                      row.set(targetField.secondary, no);
                      no = no + 1;
                    })
                  } else {
                    eval(`formData.set('${targetField.primary}', ${targetField.frontEndScript})`)
                  }
                } catch (ex) {
                  console.log(`${trigger.primary}: ${targetField.frontEndScript}, ${ex}`)
                }

              })
            }
          }
        })

        if (recalculate) {
          context.state.formData = _.cloneDeep(context.state.formData);
          context.state.interimClone = _.cloneDeep(context.state.formData);
        }
      }

      if (!sendBack && !payload.eventName)
        return;

      let clone = _.cloneDeep(context.state.formData);
      let previousClone = _.cloneDeep(context.state.previousClone);

      if (
        (payload.eventName ||
          (sendBack &&
            JSON.stringify(clone) !== JSON.stringify(context.state.previousClone))) &&
        !context.state.synchronizing
      ) {
        console.log("[SYNC] start synchronize");
        context.state.submissionClone = clone;
        context.state.synchronizing = true;
        // fix URIError in diff
        const safeDiff = (previousObj, currentObj) => {
          try {
            return diff(previousObj, currentObj) || {};
          } catch(err) {
            const jdp = jdpModule.create({
              textDiff: { minLength: Infinity } // disables all character-level diff
            });
            return jdp.diff(previousObj, currentObj) || {}
          }
        }

        try {
          let res = null;
          let syncPayload = {
            previousFormData: previousClone,
            formDataChanges: safeDiff(previousClone, clone),
            vectorId: context.state.vectorId,
            moduleId: context.state.vectorId.toString(),
            locusId: context.state.locusId,
            query: context.state.query,
            events: payload.eventName ? [payload.eventName] : [],
            eventPayload: payload.eventPayload ? payload.eventPayload : null,
            activityName: payload.activity.name,
            activityId: payload.activity.id
          };

          if (window.router && window.router.currentRoute
            && window.router.currentRoute.name == 'shareForm') {
              res = await Axios.post(`${webAppConfig.baseURL}/api/activity/formsync`, syncPayload,
                {
                  headers: {
                    Authorization: `inishare ${window.router.currentRoute.params.id}:${window.router.currentRoute.query.code}`
                  }
                })
          } else {
            let config = null;
            if (context.state.syncAbortController) {
              config = {
                signal: context.state.syncAbortController.signal
              }
            }

            res = await context.state.client.post("/api/activity/formsync", syncPayload, config);
          }

          if (res.data == null || res.data.vectorId != context.state.vectorId) {
            context.state.syncError = false;
            context.state.syncErrorRetry = 0;
            context.state.synchronizing = false;
            return;
          }

          context.state.syncError = false;
          context.state.syncErrorRetry = 0;
          context.state.synchronizing = false;

          //no new changes from formdata
          if (
            JSON.stringify(context.state.formData) ===
            JSON.stringify(context.state.submissionClone)
          ) {
            //apply changes
            console.log("[SYNC] apply changes");

            let formData = context.state.submissionClone;
            formData = patch(formData, res.data.dataChanges);

            context.commit("syncFormData", formData);
            // Trigger lists are NOT adopted from sync responses (SS06019): they derive from
            // the form design, which is fixed while the form is open, so the form-time lists
            // committed at load are already authoritative. Adopting the sync copy let a
            // truncated server list (formula triggers dropped alongside a declared-trigger
            // backendScript) silently disarm the formula's source fields after the first sync.

            if (context.state.originalForm != null) {
              context.commit('revertForm');
            }
            if (res.data.formChanges) {
              //recursive find form element
              // form changes

              res.data.formChanges.forEach(change => {
                // get
                let element = commons.findElementById(context.state.form && context.state.form.rows, change.id, null, change.index);
                // set
                applyElementFormChange(element, change);
              });
            }

            if (res.data.interaction && res.data.interaction['message'] != null) {
              alert(res.data.interaction['message']);
            }

            context.state.previousClone = _.cloneDeep(formData);

            /* §4.3.1 live-form GNey fill: the sync returns REFERENCES when a form-graph
               teammate trigger fired — the fill itself runs through the dedicated endpoint,
               never inline in the sync. View forms are not editable, so no fill there. The
               apply round-trip of our own fill clears its marker and ignores any directive it
               reports, so a fill can never chain itself into a loop. */
            const fillState = context.state.teammateFill;
            if (fillState && fillState.awaitingApplySync) {
              fillState.awaitingApplySync = false;
            } else if (Array.isArray(res.data.teammateFillTriggers)
              && res.data.teammateFillTriggers.length
              && !payload.view) {
              context.dispatch("teammateFillRun", {
                directive: res.data.teammateFillTriggers[0],
                activity: payload.activity,
              });
            }
          } else {
            console.log('signature unmatch')
            const fillState = context.state.teammateFill;
            if (fillState && fillState.awaitingApplySync) {
              fillState.awaitingApplySync = false;
            }
          }
        } catch (err) {
          if (Axios.isCancel(err)) {
            context.state.syncError = false;
            context.state.syncErrorRetry = 0;
            context.state.synchronizing = false;
          } else {
            console.error(err);
            context.state.synchronizing = false;
            context.state.syncErrorRetry = context.state.syncErrorRetry + 1;
            context.state.syncError = true;
          }
        }
      }
    },
    /* §4.3.1 live-form GNey fill. The directive is a REFERENCE ({nodeId, actionId, label}) —
       the instruction stays server-owned, resolved from the canvas by the endpoint. The user
       always wins: anything typed while GNey works is never overwritten, and the whole fill
       is one atomic Undo. Applying the values mutates formData wholesale, so the classification
       watcher round-trips a normal sync and the form's own formulas recompute over them. */
    async teammateFillRun(context, payload) {
      const current = context.state.teammateFill;
      if (current && current.status === "pending") return;

      const directive = payload.directive;
      if (!directive || !directive.nodeId || !directive.actionId || !payload.activity) return;

      const snapshot = _.cloneDeep(context.state.formData);
      const elements = commons.getAllElements(null, context.state.form) || [];
      const hiddenFields = elements
        .filter((x) => x && x.visible === false)
        .map((x) => x.fieldName || x.displayName)
        .filter(Boolean);

      /* Applies GNey's proposed values onto the live form. Shared by the fresh-call and
         cached paths. User always wins: skips anything edited since the snapshot. */
      const applyValues = (values) => {
        const clone = _.cloneDeep(context.state.formData);
        const undoValues = {};
        const applied = {};
        const filled = [];
        Object.entries(values).forEach(([key, value]) => {
          if (JSON.stringify(context.state.formData[key]) !== JSON.stringify(snapshot[key]))
            return;
          undoValues[key] = clone[key] === undefined ? null : _.cloneDeep(clone[key]);
          applied[key] = _.cloneDeep(value);
          clone[key] = value;
          filled.push(key);
        });

        if (!filled.length) {
          context.state.teammateFill = Object.assign({}, context.state.teammateFill, {
            status: "error",
            message: "GNey had nothing to fill from the provided content.",
          });
          return;
        }

        context.state.teammateFill = Object.assign({}, context.state.teammateFill, {
          status: "done",
          filled: filled,
          undoValues: undoValues,
          applied: applied,
          awaitingApplySync: true,
        });
        context.state.formData = clone;
      };

      /* D4 cache: same action + same file/image inputs on this open form -> reuse the
         earlier result, no second provider call. Keyed on the file-kind field VALUES
         (the fill's real input besides the form itself); per form session (cleared on
         loadForm), never shared. */
      const fileKinds = { 10: true, 11: true, 12: true, 13: true };
      const fileValues = {};
      elements
        .filter((x) => x && fileKinds[Number(x.type)])
        .forEach((x) => {
          const key = x.fieldName || x.displayName;
          if (key && snapshot[key] != null) fileValues[key] = snapshot[key];
        });
      const cacheKey = `${directive.nodeId}|${directive.actionId}|${JSON.stringify(fileValues)}`;

      context.state.teammateFill = {
        status: "pending",
        label: directive.label || "GNey",
        nodeId: directive.nodeId,
        actionId: directive.actionId,
        message: null,
        filled: [],
        undoValues: null,
        applied: null,
        awaitingApplySync: false,
      };

      /* $form actions are explicit clicks — the user asked for a FRESH run, and their inputs
         are the whole form (the file-keyed cache would serve stale results for text-driven
         actions). Cache applies to change-triggered fills only. */
      const cacheable = directive.nodeId !== "$form";
      const cached = cacheable ? context.state.teammateFillCache[cacheKey] : null;
      if (cached) {
        applyValues(_.cloneDeep(cached));
        return;
      }

      try {
        const res = await context.state.client.post("/api/Activity/TeammateFill", {
          vectorId: context.state.vectorId,
          activityId: payload.activity.id,
          nodeId: directive.nodeId,
          actionId: directive.actionId,
          formData: snapshot,
          dirtyFields: [],
          hiddenFields: hiddenFields,
        });

        const data = res.data || {};
        if (data.error || !data.values) {
          context.state.teammateFill = Object.assign({}, context.state.teammateFill, {
            status: "error",
            message: data.message || "GNey could not fill the form this time.",
          });
          return;
        }

        if (cacheable)
          context.state.teammateFillCache[cacheKey] = _.cloneDeep(data.values);
        applyValues(data.values);
      } catch (err) {
        console.error(err);
        context.state.teammateFill = Object.assign({}, context.state.teammateFill, {
          status: "error",
          message: "GNey could not fill the form this time.",
        });
      }
    },
    teammateFillUndo(context) {
      const fill = context.state.teammateFill;
      if (!fill || fill.status !== "done" || !fill.undoValues) return;

      const clone = _.cloneDeep(context.state.formData);
      fill.filled.forEach((key) => {
        /* only revert values still exactly as GNey left them */
        if (JSON.stringify(clone[key]) !== JSON.stringify(fill.applied[key])) return;
        if (fill.undoValues[key] === null) delete clone[key];
        else clone[key] = fill.undoValues[key];
      });

      context.state.teammateFill = null;
      context.state.formData = clone;
    },
    teammateFillDismiss(context) {
      context.state.teammateFill = null;
    },
    async importData(context, payload) {
      return await context.state.client.post("/api/collection/import", payload);
    },
    async loadDocument({ state }, payload) {
      return await state.client.post('/api/activity/document', payload);
    },
    async shareForm({ state }, payload) {
      return await state.client.post('/api/activity/', payload);
    },
    async submitActivity({ state }, payload) {
      let activityApi = "/api/activity/";

      if (
        state.activityId === payload.activityId && 
        state.activityComplexity === 0 && 
        (
          payload.activityId === 'create' || 
          payload.entryIds.length === 1
        )
      ) {
        activityApi = "/api/activity/x";
      }

      if (
        window.router
        && window.router.currentRoute
        && window.router.currentRoute.name == 'shareForm'
      ) {
        return await Axios.post(`${webAppConfig.baseURL}${activityApi}`, payload,
          {
            headers: {
              Authorization: `inishare ${window.router.currentRoute.params.id}:${window.router.currentRoute.query.code}`
            }
          })
      } else {
        return await state.client.post(activityApi, payload);
      }
    },
    async submitActivityStateless({ state }, payload) {
      return await state.client.post('/api/activity/', payload);
    },
    async saveUserPreference({ state }, payload) {
      const workspaceId = (
        state.workspace &&
        state.workspace.id
      ) || LocalStorageService.getWorkspaceId(webAppConfig.baseServer);

      if (workspaceId) {
        LocalStorageService.clearWorkspaceById(webAppConfig.baseServer, workspaceId);
      }

      return await state.client.post('/api/preference', payload);
    },
    updateDocumentId(context, payload) {
      context.commit("updateDocumentId", payload);
    },
    async sendComment({ state }, payload) {
      return await state.client.post('/api/activity/comment', payload)
    },
    async reactActivity({ state }, payload) {
      return await state.client.post('/api/activity/react', payload)
    },
    async retrieveHistory({ state }, { moduleId, id, history, currentPage = 0, archive }) {
      let url = `/api/activity/history/?m=${moduleId}&id=${id}&history=${history}&p=${currentPage}`;

      if (archive) {
        url += "&a=true";
      }

      return await state.client.get(url);
    },
    // P3 admin "view original": the server-side revision trail of an edited/deleted comment
    // (FETIAS docs/collaboration-comment-edit-delete-proposal.md §12 P3). Administrator /
    // Workspace Admin only — 403 { reason: "notAdmin" } otherwise.
    async loadCommentRevisions({ state }, { id, type, locusId }) {
      return await state.client.get(`/api/activity/commentrevisions?id=${encodeURIComponent(id)}&type=${type ? 1 : 0}&locusId=${encodeURIComponent(locusId)}`);
    },
    async retrieveNotification({ state }, payload) {
      return await state.client.get(`/api/activity/notification/?m=${payload.moduleId}&id=${payload.id}`)
    }
  }
});
